import { useCallback, useEffect, useState } from 'react';
import { Stack, CircularProgress, Box } from '@mui/material';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';
import PublicAPI from '@/api/TreedisPublicDAO';
import socket from '@/components/util/socket';
import { formData } from '@/utils/formData';
import { NoteForm } from '../forms/regular/NoteForm';
import { NoteReporterBar } from '../NoteReporterBar';
import { getBaseURL } from '@/components/util';
import { pickTagThreadData } from '../../transformers';
import { useNotesContext } from '../../hooks/useNotesContext';
import { TNote } from '../../types';
import { DeleteReplyModal } from '../modals/DeleteReplyModal';
import { NoteMessage } from '../message/NoteMessage';
import { NoteMainMessage } from '../message/NoteMainMessage';
import { NoteFormat, NoteIntegration, NoteType, NotePriority } from '../../enums';
import { BazanForm } from '../forms/bazan';
import { ICLFaultType } from '../../enums/icl';
import { useNotesStore } from '../../stores/notes.store';
import { MessageForm } from '../forms/message';

export type HandleAddArgs = {
  title: string;
  description: string;
  messageId?: string | number;
  isMainMessage?: boolean;
  format: NoteFormat;
  SAPData: Record<string, any>;
  assigneeId?: string;
  tagId?: number;
  taskManager?: {
    priority: NotePriority;
    dueDate: Date;
    backlog: boolean;
    noteType: NoteType;
  };
  icl?: {
    isEquipmentFailure: boolean;
    availableFrom: Date;
    faultType: ICLFaultType;
    faultListIndex: number;
  };
  position?: {
    x: number;
    y: number;
    z: number;
  };
};

export const formatDate = (dateString: string) => {
  const date = moment(dateString);

  if (!date.isValid()) {
    console.log('thread formatDate is invalid for ', dateString);
    return '';
  }

  return date.format('MMMM D, h:mm A');
};

type Props = {
  onNoteCreated?: (note: TNote) => void;
  onNewNoteCancel?: () => void;
};

export function Note({ onNoteCreated, onNewNoteCancel }: Props) {
  const {
    collaborators,
    isGuest,
    newThread,
    setNewThread,
    userId,
    tour,
    showCaseTemp,
    showcase,
    addThreadToSpace,
    setAddingWorkflow,
    setNoteToEdit,
    mediaUrls,
    setMediaUrls,
    getLocation,
    isRtl,
    updateMessages,
    isTourAdmin,
    controls,
    noteToEdit,
    addingWorkflow,
    integration,
    tags,
  } = useNotesContext();
  const { updateNote, updateMessage, setNotes } = useNotesStore((state) => state.actions);
  const selectedNote = useNotesStore((state) => state.selectedNote);

  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [files, setFiles] = useState([]);
  const [isCompleted, setIsCompleted] = useState(selectedNote?.isCompleted || false);

  const isNewThread = !!newThread || addingWorkflow?.active;

  const foundUser = collaborators?.find((c) => c.id === userId);
  const threadUser = collaborators?.find((c) => c.id === selectedNote?.userId);

  useEffect(() => {
    if (selectedNote && selectedNote.messages) {
      const threadMessages = selectedNote.messages?.sort((a, b) => {
        const dateA: Date = new Date(a.createdAt);
        const dateB: Date = new Date(b.createdAt);

        return dateA.getTime() - dateB.getTime();
      });
      setMessages(threadMessages);
      setIsCompleted(selectedNote?.isCompleted);
    } else {
      setMessages([]);
      setIsCompleted(false);
    }
  }, [selectedNote]);

  const parseUsernames = (text: string) => {
    let mentions = [];
    const regex = /<span[^>]*>.*?@(.*?)<\/span>/g;

    mentions = [...text.matchAll(regex)].map((match) => {
      const rawText = match[1];
      return rawText.replace(/&nbsp;/g, ' ').trim();
    });

    mentions.push(...collaborators.filter((collaborator) => mentions.includes(collaborator.name)));

    return mentions;
  };

  const handleAdd = async ({
    title,
    description,
    format,
    SAPData,
    taskManager,
    tagId,
    assigneeId: assigneeName,
    icl,
    position,
  }: HandleAddArgs) => {
    let resultMessage = '';
    const createdAt = moment().format('YYYYMMDD HHmm');
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    description = description || '';
    const mentionedUsers = description ? parseUsernames(description) : [];
    const tourLink = getTourLink();

    let dataToAdd: any = {
      files,
      tourId: tour.id,
      description,
      userId,
      title,
      format,
      isMainMessage: true,
      reporter: foundUser?.name || 'Guest',
      SAPFields:
        format === NoteFormat.Bazan
          ? JSON.stringify({
              ...SAPData,
              assignee: assigneeName,
              reporter: foundUser?.email ? foundUser.email.split('@')[0] : '',
              createdAt,
              description,
              title,
            })
          : {},
      icl: integration === NoteIntegration.ICL && icl ? JSON.stringify(icl) : undefined,
      mentionedUsers: JSON.stringify(mentionedUsers),
      tourLink,
      isRtl,
      timezone,
      priority: taskManager?.priority,
      dueDate: taskManager?.dueDate,
      assignee: assigneeName,
      backlogEnabled: taskManager?.backlog,
      type: taskManager?.noteType,
    };

    if (typeof tagId === 'number' || newThread || noteToEdit?.note) {
      const tagData = tags.find((t) => t.id === tagId);
      if (tagData) {
        dataToAdd = {
          ...dataToAdd,
          ...pickTagThreadData(tagData),
          sid: uuidv4(),
          tagId,
        };
      } else if (newThread) {
        delete newThread.id;
        dataToAdd = {
          ...newThread,
          ...dataToAdd,
        };
      }

      if (noteToEdit?.note) {
        const { note } = noteToEdit;

        dataToAdd = {
          id: note.id,
          sid: note.sid,
          messages: note.messages,
          ...dataToAdd,
          positionX: position.x ?? note.positionX,
          positionY: position.y ?? note.positionY,
          positionZ: position.z ?? note.positionZ,
          stemVectorX: note.stemVectorX,
          stemVectorY: note.stemVectorY,
          stemVectorZ: note.stemVectorZ,
        };
      }

      const saveFormData = formData.create(dataToAdd);
      const result = await PublicAPI.saveThread({
        data: saveFormData,
      });

      resultMessage = result?.message;

      if (resultMessage === 'Success') {
        const { savedThread, reportStatus } = result;
        if (reportStatus === 'error') {
          toast.error('Failed to send SAP report to BAZAN');
        }
        if (reportStatus === 'success') {
          toast.success('SAP report sent to BAZAN successfully');
        }

        if (noteToEdit?.note) {
          updateNote(savedThread.id, savedThread);
          updateMessage(savedThread.id, noteToEdit.note.messages.find((m) => m.isMainMessage).id, {
            description: savedThread.message.description,
            mediaUrls: savedThread.message.mediaUrls,
            mobileMediaUrls: savedThread.message.mobileMediaUrls,
          });
        } else {
          await showCaseTemp.tag.remove(savedThread.sid);

          if (showcase?.Mattertag?.registerIcon) {
            await showcase.Mattertag.registerIcon(
              `thread`,
              savedThread.iconUrl,
            ).catch((e) => {
              console.error('[Note] Error registering thread icon:', e);
            });
          } else {
            console.error('[Note] showcase.Mattertag.registerIcon is not available');
          }

          savedThread.customIcon = 'thread';
          const note = await addThreadToSpace(savedThread);
          setNotes((prevThreads) => [...prevThreads, note]);

          setNewThread(null);
          setAddingWorkflow(null);
          onNoteCreated?.(savedThread);
          controls.current.inputs.selection = null;
          socket.emit('thread_added', { ...savedThread, room: tour.slug });
        }
      }
    }

    setNoteToEdit(null);
    setMediaUrls([]);
    return resultMessage;
  };

  const handleSubmitMessage = async ({
    description,
    messageId,
  }: {
    description: string;
    messageId?: number;
  }) => {
    let resultMessage = '';
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    description = description || '';
    const mentionedUsers = description ? parseUsernames(description) : [];
    const tourLink = getTourLink();

    const filteredUrls = mediaUrls
      .filter((urlObj) => urlObj.flaggedToDelete)
      ?.map((urlObj) => urlObj.src);
    const dataToAdd = formData.create({
      files,
      threadId: selectedNote.id,
      description,
      userId,
      messageId,
      mediaUrls: filteredUrls,
      isMainMessage: false,
      mentionedUsers: JSON.stringify(mentionedUsers),
      tourLink,
      isRtl,
      timezone,
    });

    const result = await PublicAPI.saveThreadMessage({
      data: dataToAdd,
    });

    resultMessage = result?.message;
    if (resultMessage === 'Success') {
      const { savedMessage } = result;

      const newMessages = updateMessages(selectedNote, savedMessage);
      updateNote(selectedNote.id, { messages: newMessages });

      if (messageId) {
        socket.emit('message_edited', {
          ...result.savedMessage,
          savedTitle: result.savedTitle,
          room: tour.slug,
        });
      } else {
        socket.emit('message_added', { ...result.savedMessage, room: tour.slug });
      }
    }

    setNoteToEdit(null);
    setMediaUrls([]);
    return resultMessage;
  };

  const renderMessages = useCallback(() => {
    return messages.map((m) => {
      return m?.isMainMessage ? (
        <NoteMainMessage
          key={m.id}
          isCompleted={isCompleted}
          message={m}
          form={
            <NoteForm
              handleAdd={handleAdd}
              files={files}
              setFiles={setFiles}
              isNewThread={false}
              foundUser={foundUser}
              isMainMessage={m?.isMainMessage}
              onCloseDrawer={onNewNoteCancel}
              initialValues={
                noteToEdit
                  ? {
                      title: noteToEdit?.note?.title,
                      description: noteToEdit?.message?.description,
                      assigneeId: noteToEdit?.note?.assignee,
                      priority: Object.values(NotePriority).some(
                        (v) => v === noteToEdit?.note?.priority,
                      )
                        ? (noteToEdit?.note?.priority as NotePriority)
                        : undefined,
                      dueDate: noteToEdit?.note?.dueDate
                        ? new Date(noteToEdit.note.dueDate)
                        : undefined,
                      backlogEnabled: noteToEdit?.note?.backlogEnabled,
                      noteType: Object.values(NoteType).some((v) => v === noteToEdit?.note?.type)
                        ? (noteToEdit?.note?.type as NoteType)
                        : undefined,
                      isEquipmentFailure: noteToEdit?.note?.notesICLData?.isEquipmentFailure,
                      availableFrom: noteToEdit?.note?.notesICLData?.availableFrom
                        ? new Date(noteToEdit.note.notesICLData.availableFrom)
                        : undefined,
                      faultType: noteToEdit?.note?.notesICLData?.faultType as ICLFaultType,
                      faultListIndex: noteToEdit?.note?.notesICLData?.faultListIndex,
                      tagId: noteToEdit?.note?.tagId,
                    }
                  : undefined
              }
            />
          }
        />
      ) : (
        <NoteMessage
          key={m.id}
          message={m}
          form={
            <MessageForm
              files={files}
              setFiles={setFiles}
              onSubmit={({ description }) =>
                handleSubmitMessage({
                  description,
                  messageId: m.id,
                })
              }
              message={m}
            />
          }
        />
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    messages,
    newThread,
    threadUser?.name,
    mediaUrls,
    files,
    isTourAdmin,
    foundUser?.id,
    selectedNote?.title,
    selectedNote?.messages,
    isCompleted,
  ]);

  const getTourLink = () => {
    let linkValue = getLocation();

    const segments = window.location.href.split('/');
    const slug = segments[segments.length - 1].split('?')[0];
    if (tour.slug !== slug) {
      const params = linkValue.split('?')[1];
      linkValue = `${linkValue
        .split('/')
        .map((part, index) => (index === 4 ? tour.slug : part))
        .join('/')}${params ? `?${params}` : ''}`;
    }
    return linkValue;
  };

  return (
    <Stack gap="10px" direction="column" height="100%">
      <Stack
        direction="column"
        height="100%"
        alignItems="space-between"
        // gap="16px"
        flexGrow={noteToEdit ? 0 : 1}
      >
        {isNewThread && (
          <NoteReporterBar
            name={foundUser?.name}
            editMode={!!noteToEdit}
            isNewThread={isNewThread}
          />
        )}
        {!isNewThread && (
          <Stack direction="column" gap={2} mb={2}>
            {renderMessages()}
          </Stack>
        )}
        {isLoading && (
          <Stack
            width="100%"
            alignItems="center"
            justifyContent="center"
            height="100%"
            position="absolute"
            sx={{ backgroundColor: 'rgba(255, 255, 255, 0.7)', zIndex: 100 }}
          >
            <CircularProgress />
          </Stack>
        )}

        {!noteToEdit && !isNewThread ? (
          <Box
            sx={{
              position: 'sticky',
              bottom: 0,
              maxHeight: '700px',
              zIndex: '1600',
              background: '#F1F1F1',
              marginTop: 'auto',
              flexGrow: 0,
            }}
          >
            <MessageForm
              files={files}
              setFiles={setFiles}
              onSubmit={({ description }) =>
                handleSubmitMessage({
                  description,
                })
              }
            />
          </Box>
        ) : null}

        {!noteToEdit && isNewThread && !isGuest && (
          <Stack
            sx={{
              background: '#F1F1F1',
              marginTop: isNewThread ? 0 : 'auto',
              flexGrow: isNewThread ? 1 : 0,
              ...(isNewThread
                ? {}
                : {
                    position: 'sticky',
                    bottom: 0,
                    maxHeight: '700px',
                    zIndex: '1600',
                  }),
            }}
          >
            {/* Forms for adding notes */}
            {/* Bazan and NoteForm have a lot of duplication but we need to keep bazan as is to not break it */}
            {integration === NoteIntegration.Bazan ? (
              <BazanForm
                handleAdd={handleAdd}
                files={files}
                setFiles={setFiles}
                isNewThread={isNewThread}
                foundUser={foundUser}
                onCloseDrawer={onNewNoteCancel}
              />
            ) : (
              <NoteForm
                isMainMessage
                handleAdd={handleAdd}
                files={files}
                setFiles={setFiles}
                isNewThread={isNewThread}
                foundUser={foundUser}
                onCloseDrawer={onNewNoteCancel}
              />
            )}
          </Stack>
        )}

        <DeleteReplyModal setIsLoading={setIsLoading} />
      </Stack>
    </Stack>
  );
}
